import { Injectable, inject } from '@angular/core';
import { FormGroup } from '@angular/forms';
import { ResponseDto } from '@models/exceptions/response-dto';
import { HandlerErrorMessage } from 'src/app/core/utilities/handler-error-message';
import { MessageErrorResolver } from 'src/app/core/utilities/message-error-resolver';
import { findUserFormControlByApiLabel } from './user-form-api-error-control-finder';
import { withNormalizedUserFormErrorLabels } from './user-form-api-error-labels.util';
import { registerUserFormApiErrorParams } from './user-form-api-error-params.config';
import { UserFormForApiErrors } from './user-form-api-error.types';
import {
  clearDocumentApiErrors,
} from './user-form-document-api-error.util';

/**
 * Erros de validação retornados pela API (type 1) no cadastro de usuário.
 * Instância dedicada do MessageErrorResolver; não altera o singleton global.
 */
@Injectable()
export class UserFormApiErrorService {
  private readonly messageErrorResolver = inject(MessageErrorResolver);
  private readonly handlerErrorMessage = inject(HandlerErrorMessage);

  readonly resolver = this.messageErrorResolver.clone();

  constructor() {
    registerUserFormApiErrorParams(this.resolver);
  }

  resolveApiErrors(error: ResponseDto<unknown>, userForm: FormGroup): void {
    const form = userForm.controls as unknown as UserFormForApiErrors;

    clearDocumentApiErrors(form.documents);

    const responseDto = withNormalizedUserFormErrorLabels(error);
    this.resolver.resolveMessageWithReactiveForm(
      responseDto,
      (label, errorMessage) =>
        findUserFormControlByApiLabel(form, label, errorMessage),
    );

    this.syncDocumentApiErrorDisplayKeys(form);
  }

  fieldErrorMessage(
    fg: FormGroup,
    key: string,
    arrayContext?: string | null,
  ): string {
    const messageKey = arrayContext ? `${arrayContext}_${key}` : key;
    let apiMessage = this.resolver.getFieldApiMessage(messageKey);

    const ctrl = fg.get(key);
    if (!apiMessage && ctrl?.errors?.['apiError']) {
      apiMessage = this.resolver.getFieldApiMessage(key);
    }

    if (apiMessage) {
      return apiMessage;
    }

    return this.handlerErrorMessage.showErrorMessageForKey(fg, key);
  }

  private syncDocumentApiErrorDisplayKeys(form: UserFormForApiErrors): void {
    const message = this.resolver.getFieldApiMessage('docTxDocumento');
    if (!message) {
      return;
    }

    for (const documentFg of form.documents.controls) {
      if (!documentFg.get('docTxDocumento')?.errors?.['apiError']) {
        continue;
      }

      const tipo = documentFg.get('docTxTipo')?.value;
      if (tipo) {
        this.resolver.setFieldApiMessage(`${tipo}_docTxDocumento`, message);
      }
    }
  }
}
